"use client";

import { apiUrl } from "./basePath";

export const INVITE_SESSION_KEYS = {
  scopedJwt: "assessment_invite_scoped_jwt",
  assessmentAssignedId: "assessment_invite_assigned_id",
  assessmentSetId: "assessment_invite_set_id",
  assessmentType: "assessment_invite_type",
  name: "assessment_invite_name",
  email: "assessment_invite_email",
  role: "assessment_invite_role",
  company: "assessment_invite_company",
  allowProctoring: "assessment_invite_allow_proctoring",
  allowVerification: "assessment_invite_allow_verification",
  studentId: "assessment_invite_student_id",
  /** Extracted resume text for the AI Interview, from the pre-assessment
   *  resume step. Kept on the session rather than the form draft because the
   *  draft is cleared on the way into the test, and the interview — a route
   *  later — is the only thing that reads it. */
  resumeText: "assessment_invite_resume_text",
} as const;

type JsonRecord = Record<string, unknown>;

async function post(action: string, body: JsonRecord): Promise<JsonRecord> {
  const response = await fetch(apiUrl(`/api/invite/${action}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as JsonRecord;
  if (!response.ok) {
    const nested = payload.data as JsonRecord | undefined;
    throw new Error(String(payload.message || nested?.message || "Request failed."));
  }
  return payload;
}

export function unwrap(payload: JsonRecord): JsonRecord {
  const first = payload.data;
  if (!first || typeof first !== "object") return payload;
  const second = (first as JsonRecord).data;
  return second && typeof second === "object" ? (second as JsonRecord) : (first as JsonRecord);
}

export async function resolveInvite(inviteToken: string) {
  return unwrap(await post("resolve", { inviteToken }));
}

export async function requestInviteOtp(inviteToken: string, email: string) {
  return unwrap(await post("request", { inviteToken, email }));
}

export async function resendInviteOtp(inviteToken: string, email: string) {
  return unwrap(await post("resend", { inviteToken, email }));
}

export async function verifyInviteOtp(inviteToken: string, email: string, otp: string) {
  return unwrap(await post("verify", { inviteToken, email, otp }));
}

function put(key: string, value: unknown) {
  if (value !== undefined && value !== null && value !== "") {
    sessionStorage.setItem(key, String(value));
  }
}

export function persistInviteSession(data: JsonRecord, invite: JsonRecord, email: string) {
  const token = data.token;
  const assignedId = data.assessment_assigned_id;
  if (!token || !assignedId) throw new Error("Verification did not create an assessment session.");

  put(INVITE_SESSION_KEYS.scopedJwt, token);
  put(INVITE_SESSION_KEYS.assessmentAssignedId, assignedId);
  put(INVITE_SESSION_KEYS.assessmentSetId, invite.assessmentSetId || data.assessment_set_id);
  put(INVITE_SESSION_KEYS.assessmentType, invite.assessmentType || data.assessment_type);
  put(INVITE_SESSION_KEYS.name, invite.assessmentName || data.assessment_name);
  put(INVITE_SESSION_KEYS.email, data.email || email);
  put(INVITE_SESSION_KEYS.role, data.role || invite.role);
  put(INVITE_SESSION_KEYS.company, data.company || invite.company);
  put(INVITE_SESSION_KEYS.studentId, invite.studentId);
  put(INVITE_SESSION_KEYS.allowProctoring, invite.allowProctoring === true);
  put(INVITE_SESSION_KEYS.allowVerification, invite.allowVerification !== false);
  // Compatibility for the existing AI Interview runner while it is migrated.
  put("ai_interview_scoped_jwt", token);
  put("ai_interview_assessment_assigned_id", assignedId);
}

/**
 * The assignment this tab's session was minted for — unique per candidate per
 * sitting, and set by both entry points (the OTP invite above, and a signed-in
 * student trading their login for a scoped session in `studentSession.ts`).
 * It is what scopes the stored attempt, so a later assignment into the same
 * float cannot resume this one and two candidates on one browser cannot share
 * a record. The invite-less demo route has no assignment and gets "local".
 */
export function attemptOwnerId(): string {
  try {
    return sessionStorage.getItem(INVITE_SESSION_KEYS.assessmentAssignedId) || "local";
  } catch {
    return "local"; // storage disabled — the attempt lives for this page only
  }
}

/** Whether the readiness check should perform identity verification. Missing
 *  state keeps the historical behaviour for demo and older invite sessions. */
export function isBiometricVerificationEnabled(): boolean {
  try {
    return sessionStorage.getItem(INVITE_SESSION_KEYS.allowVerification) !== "false";
  } catch {
    return true;
  }
}

/**
 * Drops everything the invite put in this tab. Called once the assessment is
 * submitted, so a finished sitting cannot be walked back into from the
 * sign-in screen or the back button.
 */
export function clearInviteSession() {
  try {
    for (const key of Object.values(INVITE_SESSION_KEYS)) sessionStorage.removeItem(key);
  } catch {
    /* storage disabled — nothing was stored to clear */
  }
}
